import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { Access, AuthData, College, Company, S10Response, Training, UserData, UserDataToUpdate, UserHealthPlan, UserRightHolders, UserVacationsResume } from '../../../interfaces/auth.interface';
import { cleanDuplicates } from '../../../utils/data';


export type SeeInformationAs = "grid" | "card";
export interface Auth {
    // userToken: string | null;
    userHasProfilePicture: boolean | null,
    usersWithoutProfilePicture: string[],
    userData: UserData | null;
    userDataToUpdate: UserDataToUpdate | null;
    sessionData: AuthData | null;
    companyselected: Company | null;
    isLogged: boolean | null;
    isLoading: boolean;
    autherror: string;
    connectionId: string | null;
    userMenu: Access[];
    colleges: College[],
    userRightHolders: UserRightHolders[];
    userTraining: Training[];
    userHealthPlan: UserHealthPlan[];
    userVacationsResume: UserVacationsResume[];
    seeInformationAs: SeeInformationAs;
}

const initialState: Auth = {
    // userToken: null,
    usersWithoutProfilePicture: [],
    userHasProfilePicture: null,
    userData: null,
    userDataToUpdate: null,
    sessionData: null,
    companyselected: null,
    isLogged: null,
    isLoading: false,
    autherror: "",
    connectionId: null,
    userMenu: [],
    colleges: [],
    userRightHolders: [],
    userTraining: [],
    userHealthPlan: [],
    userVacationsResume: [],
    seeInformationAs: "card",
}

const normalizeTitle = (title: string) => {
    const normalizedTitle = title.toLowerCase()
      .normalize('NFKD') // Normalizar caracteres especiales
      .replace(/[\u0301-\u036F]/g, '') // Eliminar acentos
      .replace(/[aeiouy]/g, function (char) {
        return char.replace(/á|é|í|ó|ú|ä|ë|ï|ö|ü/g, char[0]);
      }) // Reemplazar vocales con acento por su equivalente sin acento
      .replace(/[^\w-]+/g, '-') // Reemplazar otros caracteres por guiones
      .replace(/--/g, '-'); // Eliminar dobles guiones
    return normalizedTitle;
};

const getMenuData = (menuOptions: Access[], ParentId: number | null = null, level=0, parentPath = ""): Access[] => {
    const itemsLevel = menuOptions?.filter(m => m.Level === level && m.ParentId === ParentId)
                        .sort((a, b) => a.Order - b.Order);
    const itemsNextLevel = menuOptions?.filter(m => m.Level > level);
    const finalItems: Access[] = []
    for (let index = 0; index < itemsLevel?.length; index++) {
        const item = itemsLevel[index]
        const currentPath = `${parentPath}/${normalizeTitle(item.Name)}`;
        const children = getMenuData(
            itemsNextLevel, 
            item.SecurityAccessId, 
            level + 1,
            currentPath 
        );
        finalItems.push({
            ...item,
            children: children,
            urlPath: currentPath,
        })
        
    }
    return finalItems;
}

const handlerSetCompanySelected = (state: any, action: PayloadAction<Company>) => {
    const companyData = action.payload;
    state.companyselected = companyData;
    const userMenu = getMenuData(companyData.Roles?.Access);
    // TODO: this is only for test
    if (['ncortez@s10peru.com', 'lsarayasi@s10peru.com'].includes(state.sessionData?.UserName)) {
        userMenu.map((parent: Access) => ({
            ...parent,
            children: parent.Name === 'Mi Portal' 
                    ? parent?.children?.push(...[{
                        ...parent.children[0],
                        Id: 0,
                        Name: 'marcaciones',
                        ItemPath: 'marcaciones',
                        urlPath: '/mi-portal/mis-marcaciones',
                    }])
                    :   parent.Name === 'Administración' 
                            ? parent?.children?.push(...[{
                            ...parent.children[0],
                            Id: 1,
                            Name: 'Emails Rechazados',
                            ItemPath: 'emails-rechazados',
                            urlPath: '/administracion/email-rechazados',
                        }, {
                            ...parent.children[0],
                            Id: 1,
                            Name: 'Configuración de Emails',
                            ItemPath: 'configuracion-emails',
                            urlPath: '/administracion/configuracion-emails',
                        }, {
                            ...parent.children[0],
                            Id: 1,
                            Name: 'Descargar Boletas de Pago',
                            ItemPath: 'boletas-de-pago',
                            urlPath: '/administracion/descargar-boletas-de-pago',
                        }])
                        : parent?.children,
        }))
    }
    state.userMenu = userMenu;
    localStorage.setItem('companySelected', JSON.stringify(action.payload));
    localStorage.setItem('userMenu', JSON.stringify(userMenu));
    window.location.replace('/');
}

export const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        logout: (state) => {
            const savedUSername = localStorage.getItem("rememberedUsername");
            localStorage.clear();
            state = {...initialState, isLogged: false};
            if (savedUSername) {
                localStorage.setItem("rememberedUsername", savedUSername);
            }
            window.location.reload()
            
        },
        setAuthData: (state, {payload}: PayloadAction<AuthData>) => {
            // state = {
            //     ...initialState,
            //     sessionData: payload,
            //     isLogged: true,
            // };
            
            state.companyselected = null;
            state.sessionData = payload;
            state.isLogged = true;
            localStorage.setItem('sessionData', JSON.stringify(payload));
            if (payload.Companies?.length === 1) {
                handlerSetCompanySelected(state, {payload: payload.Companies[0], type: "company"})
            }
            state.isLoading = false;
        },
        setUserWithoutImage: (state, {payload}: PayloadAction<string>) => {
            const data = state.usersWithoutProfilePicture;
            if (data.includes(`${payload}`)) {
                return;
            }
            state.usersWithoutProfilePicture = [...data, `${payload}`];
        },
        setUserHasProfilePicture: (state, {payload}: PayloadAction<boolean>) => {
            state.userHasProfilePicture = payload;
        },
        setCompanySelected: handlerSetCompanySelected,
        loadAuthData: (state) => {
            state.isLoading = true;
            const sessionData = localStorage.getItem('sessionData');
            const companyData = localStorage.getItem('companySelected');
            const userMenuLS = localStorage.getItem('userMenu');
            if (!sessionData) {
                state.isLogged = false;
                state.isLoading = false;
                return
            }
            if (sessionData) {
                state.sessionData = JSON.parse(sessionData);
                state.isLogged = true;
            }
            if (companyData && userMenuLS) {
                state.companyselected = JSON.parse(companyData);
                let  userMenu = JSON.parse(userMenuLS);
                state.userMenu = userMenu;
            }
            state.isLoading = false;
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload
        },
        setAuthError: (state, action: PayloadAction<string>) => {
            state.autherror = action.payload
        },
        setIdConnection: (state, action: PayloadAction<string>) => {
            state.connectionId = action.payload;
            localStorage.setItem("connectionId", action.payload);
        },
        refreshConnection: (state) => {
            
        },
        setUserData: (state, action: PayloadAction<UserData>) => {
            state.userData = action.payload;
        },
        setUserDataToUpdate: (state, action: PayloadAction<UserDataToUpdate>) => {
            state.userDataToUpdate = action.payload;
        },
        confirmUserDataUpdated: (state) => {
            const userDataToUpdate = state.userDataToUpdate || {};
            state.userData = {
                ...state.userData as any,
                ...userDataToUpdate,
            };
            state.userDataToUpdate = null;
        },
        setUserRightHolders: (state, action: PayloadAction<UserRightHolders[]>) => {
            state.userRightHolders = action.payload;
        },
        setColleges: (state, {payload}: PayloadAction<S10Response<College[]>>) => {
            const colleges: College[] = cleanDuplicates([...state.colleges, ...payload.data], "Descripcion");
            colleges.sort((a, b) => a.Descripcion.localeCompare(b.Descripcion));
            state.colleges = colleges;
        },
        setUserTraining: (state, {payload}: PayloadAction<S10Response<Training[]>>) => {
            // const userTraining: Training[] = cleanDuplicates([...state.userTraining, ...payload.data], "NroIdentificadorCapacitacion")
            state.userTraining = payload.data
            
        },
        setUserHealthPlan: (state, {payload}: PayloadAction<S10Response<UserHealthPlan[]>>) => {
            state.userHealthPlan = payload.data;
        },
        setVacationsResume: (state, {payload}: PayloadAction<S10Response<UserVacationsResume[]>>) => {
            console.log('payload.data', payload.data)
            state.userVacationsResume = payload.data;
        },
        setSeeInformationAs: (state, {payload}: PayloadAction<SeeInformationAs>) => {
            state.seeInformationAs = payload;
        },
        // refreshToken: (state, action: PayloadAction<string>) => {
        //     // state.userToken = action.payload;
        //     localStorage.setItem('userToken', action.payload);
        // },
    }
})


export const { 
    logout,
    setUserWithoutImage,
    setUserHasProfilePicture,
    setAuthData,
    setCompanySelected,
    loadAuthData,
    setLoading,
    setAuthError,
    setIdConnection,
    setUserData,
    setUserDataToUpdate,
    confirmUserDataUpdated,
    setUserRightHolders,
    setUserTraining,
    setColleges,
    setUserHealthPlan,
    setVacationsResume,
    setSeeInformationAs,
} = authSlice.actions
